import React, { useEffect, useReducer, useState } from 'react';
import TextInput from '../components/Common/TextInput';
import CallOut from '../components/Common/CallOut';
import ajaxTag from '../util/remote/ajaxTag';
import Notification from '../components/Common/Notification';
import ActivityLoader from '../components/Common/ActivityLoader';
import ColorSelector from '../components/Common/ColorSelector';
import { useTag } from '../DataStore/TagContext';
import { useStat } from '../DataStore/StatContext';
import { useTheme } from '../DataStore/ThemeContext';

const initialState = {
  tagName: '',
  projectCode: '',
  description: '',
  selectedColor: '',
  budgetAmount: '',
  status: null,          // ← use select to set this
  loading: false,
};

const reducer = (state, action) => {
  if (action.type === 'RESET') return { ...initialState };
  return { ...state, [action.type]: action.value };
};

const NewTag = () => {
  const [currentNotify, setNotify] = useState(null);
  const { getTagListing } = useTag();
  const { getProjectStatList } = useStat();
  const [state, dispatch] = useReducer(reducer, initialState);
  const {setSystemFunction} = useTheme();

  useEffect(() => {
    if (currentNotify) {
      const t = setTimeout(() => setNotify(null), 5000);
      return () => clearTimeout(t);
    }
  }, [currentNotify]);

  useEffect(() => {
    setSystemFunction(() => handleSave);
    return () => setSystemFunction(null);
  }, [state]);

  // ---------- Status Select (uses your TextInput select pattern) ----------
  const RenderStatusSelect = () => {
    const options = [
      { label: 'Active',  value: 'Active' },
      { label: 'On-Hold', value: 'On-Hold' },
      { label: 'Closed',  value: 'Closed' },
    ];

    return (
      <TextInput
        label="Project Status"
        type="select"
        leftIcon="fa fa-flag"
        placeholder={state.status?.label || 'Select project status…'}
        options={options}
        onChange={(item) => dispatch({ type: 'status', value: item })}
        leftComponent={<i className="fa fa-info-circle" title="Choose current project status"></i>}
        loading={false}
        wrap
        value={state.status}
      />
    );
  };

  const handleColorSelect = (color) => {
    dispatch({ type: 'selectedColor', value: color });
  };

  const validate = () => {
    if (!state.tagName?.trim()) return 'Project Name is required';
    if (!state.projectCode?.trim()) return 'Project Code is required';
    if (!state.status?.value) return 'Please select a project status';
    if (state.budgetAmount !== '' && isNaN(Number(state.budgetAmount))) return 'Budget must be a number';
    return null;
  };

  const handleSave = async () => {
    if (state.loading) return;

    const err = validate();
    if (err) {
      setNotify(<Notification type="danger" message={err} />);
      return;
    }

    dispatch({ type: 'loading', value: true });

    const payload = {
      project_name: state.tagName,
      code: state.projectCode,
      description: state.description,
      color: state.selectedColor || 'primary',
      status: state.status?.value || 'Active',          // ← from select
      budget: state.budgetAmount,
    };

    const server_response = await ajaxTag.addProject(payload);

    dispatch({ type: 'loading', value: false });

    if (server_response?.status === 'OK') {
      setNotify(<Notification type="success" message={server_response.message || 'Project created.'} />);
      dispatch({ type: 'RESET' });
      setTimeout(() => {getTagListing(true); getProjectStatList(true)}, 1200);
    } else {
      setNotify(
        <Notification
          type="danger"
          message={server_response?.message || 'Failed to create project. Please try again.'}
        />
      );
    }
  };

  return (
    <>
      <CallOut
        title="Projects"
        description="Create a project to organize income and expenses under one tag. Track performance and generate project-based reports anytime."
      />


    <div className="mt-4">{currentNotify}</div>

      <form className="form-horizontal" onSubmit={handleSave}>
        <TextInput
          type="text"
          label="Project Name"
          placeholder="e.g., Your company project name"
          leftIcon="fa fa-tag"
          value={state.tagName}
          onChange={(e) => dispatch({ type: 'tagName', value: e.target.value })}
          required
          wrap
        />

        <TextInput
          type="text"
          label="Project Code"
          placeholder="e.g., BP-001"
          leftIcon="fa fa-barcode"
          value={state.projectCode}
          onChange={(e) => dispatch({ type: 'projectCode', value: e.target.value })}
          required
          wrap
        />

        {/* Status select */}
        {RenderStatusSelect()}

        <TextInput
          type="textarea"
          label="Description"
          placeholder="Brief description of this project"
          leftIcon="fa fa-list"
          value={state.description}
          onChange={(e) => dispatch({ type: 'description', value: e.target.value })}
          wrap
        />

         <TextInput
              type="number"
              label="Budget Amount"
              placeholder="e.g., 500000"
              leftIcon="fa fa-coins"
              value={state.budgetAmount}
              onChange={(e) => dispatch({ type: 'budgetAmount', value: e.target.value })}
              min="0"
              step="0.01"
              wrap
            />

        <div className="form-row">
          <div className="form-group col-md-12">
            <label>Project Color</label>
            <ColorSelector selectedColor={state.selectedColor} onColorSelect={handleColorSelect} />
            {state.selectedColor && (
              <small className="form-text text-muted">
                Selected color: <strong>{state.selectedColor}</strong>
              </small>
            )}
          </div>
        </div>

     
      </form>
    </>
  );
};

export default NewTag;
